import { graphQlClient } from '@config/graphqlClient';
import { reportGraphQLErrors, markErrorReported } from '@config/errorReporting';
import TasksGraphQL from '@graphql/task.queries';
import { Task, TaskCreateInput, MaterialCreateInput, EquipmentCreateInput } from '@models/task.model';
import { RootState, store } from '@redux/store';
import {
  getTokenLocalStorage,
  getTokenSessionStorage,
} from '@config/storage';

interface GetTasksByOriginalOrganizationIdResponse {
  getTasksByOriginalOrganizationId: Task[];
}

export const getTasksByOriginalOrganizationId = async (
  originalOrganizationId: string,
): Promise<Task[]> => {
  if (!originalOrganizationId) {
    throw new Error('Original organization ID is required');
  }

  const data: GetTasksByOriginalOrganizationIdResponse = await graphQlClient.request(
    TasksGraphQL.queries.getTasksByOriginalOrganizationId,
    { originalOrganizationId }
  );

  return data.getTasksByOriginalOrganizationId || [];
};

export interface CreateSubtaskParams {
  name: string;
  description: string | null;
  taskType: string;
  status: string | null;
  otherType: string | null;
  budget: number | null;
  manHours: number | null;
  startDate: Date | string;
  endDate: Date | string;
  timeConstraint: number | null;
  selectedTypeConstraint: string | null;
  crewIds?: string[];
  materials?: MaterialCreateInput[];
  equipments?: EquipmentCreateInput[];
  deleteDocuments?: string[];
  presedentTaskId?: string[];
  presedentEventId?: string[];
  documents?: File[] | null;
  delegateOrganizationId?: string | null;
  note?: string | null;
  superintendentId?: string | null;
  projectId: string;
  parentTaskId: string;
}

// Custom file upload function for createSubtask that handles parentTaskId parameter correctly
const createSubtaskWithFileUpload = async (
  documents: File[],
  taskData: TaskCreateInput,
  parentTaskId: string
) => {
  const MAX_FILE_SIZE_MB = 50;

  // Check file sizes
  for (const file of documents) {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      throw new Error('File size exceeds the maximum allowed size');
    }
  }

  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  let token = '';
  if (rememberMe) {
    token = getTokenLocalStorage();
  } else {
    token = getTokenSessionStorage();
  }

  // Create the operations object with correct variable structure
  const operations = {
    query: TasksGraphQL.mutations.createSubtask,
    variables: {
      task: {
        ...taskData,
        file: documents.length === 1 ? null : documents.map(() => null),
      },
      parentTaskId: parentTaskId,
    },
  };

  // Create map for file locations
  const map: { [key: string]: string[] } = {};
  if (documents.length === 1) {
    map['0'] = ['variables.task.file'];
  } else {
    documents.forEach((_, index) => {
      map[index.toString()] = [`variables.task.file.${index}`];
    });
  }

  const formData = new FormData();
  formData.append('operations', JSON.stringify(operations));
  formData.append('map', JSON.stringify(map));

  // Append files
  documents.forEach((file, index) => {
    formData.append(index.toString(), file);
  });

  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'graphql', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'apollo-require-preflight': 'true',
    },
    body: formData,
  });

  if (!response.ok) {
    reportGraphQLErrors({ response: { status: response.status } }, { source: 'upload' });
    // A fresh Error, not the object just reported above — mark it explicitly so the
    // QueryCache/MutationCache `onError` handler (`reportCacheError`) recognises this
    // as the same failure and doesn't report it a second time.
    const error = new Error(`HTTP error! status: ${response.status}`);
    markErrorReported(error);
    throw error;
  }

  const result = await response.json();
  if (result.errors) {
    reportGraphQLErrors(result, { source: 'upload' });
    const error = new Error(result.errors[0]?.message || 'GraphQL error');
    markErrorReported(error);
    throw error;
  }

  return result.data;
};

export const createSubtaskAPI = async (params: CreateSubtaskParams) => {
  const taskData = {
    name: params.name,
    description: params.description,
    taskType: params.taskType,
    status: params.status === 'Unassigned' ? null : params.status,
    otherType: params.otherType,
    budget: params.budget,
    manHours: params.manHours,
    startTime: params.startDate,
    endTime: params.endDate,
    timeConstraint: params.timeConstraint,
    typeOfTimeConstraint: params.selectedTypeConstraint,
    crewIds: params.crewIds,
    projectId: params.projectId,
    presedentTaskId: params.presedentTaskId,
    presedentEventId: params.presedentEventId,
    deleteDocuments: params.deleteDocuments,
    delegateOrganizationId: params.delegateOrganizationId,
    note: params.note,
    superintendentId: params.superintendentId,
    materials: params.materials || [],
    equipments: params.equipments || [],
  } as TaskCreateInput;

  // For createSubtask with documents, we need to handle the parentTaskId parameter specially
  // since graphQlUpload puts everything under the entity name but we need parentTaskId at the top level
  const data =
    params.documents && params.documents.length > 0
      ? await createSubtaskWithFileUpload(
          params.documents,
          taskData,
          params.parentTaskId
        )
      : await graphQlClient.request(TasksGraphQL.mutations.createSubtask, {
          task: taskData,
          parentTaskId: params.parentTaskId,
        });

  return data;
};